import {
  activeDevicesIcon,
  activeInboxIcon,
  activeRmaIcon,
  activeStudiesIcon,
  activeSupportIcon,
  inactiveDevicesIcon,
  inactiveInboxIcon,
  inactiveRmaIcon,
  inactiveStudiesIcon,
  inactiveSupportIcon,
} from '@/shared/ui/assets';
import classNames from 'classnames';
import _ from 'lodash';
import { t } from 'ttag';
import type { TabIconProps } from './tabIcon.types';
import './tabIcon.scss';

/**
 * Renders a sidebar tab icon, choosing active or inactive artwork by matching the current pathname against the tab link. Switches on the tab name across Devices, Studies, Inbox, Support and Fleet, wraps the Fleet icon in a container flagged has-notification while the RMA ticket count is positive, and returns null for unknown tabs.
 *
 * @param iconLink - Route of the tab, compared against the current pathname to pick the active icon.
 * @param iconName - Tab name selecting which icon set to render.
 * @param pathname - The current pathname, matched against iconLink.
 * @param rmaTicketCount - The current RMA ticket count, used to badge the Fleet tab.
 * @returns The tab icon element, or null for an unknown tab.
 */
const TabIcon = ({ iconLink, iconName, pathname, rmaTicketCount }: TabIconProps) => {
  switch (iconName) {
    case t`Devices`: {
      return (
        <img
          src={_.includes(pathname, iconLink) ? activeDevicesIcon : inactiveDevicesIcon}
          alt={t`Devices menu icon`}
        />
      );
    }
    case t`Studies`: {
      return (
        <img
          src={_.includes(pathname, iconLink) ? activeStudiesIcon : inactiveStudiesIcon}
          alt={t`Studies menu icon`}
        />
      );
    }
    case t`Inbox`: {
      return (
        <img
          src={_.includes(pathname, iconLink) ? activeInboxIcon : inactiveInboxIcon}
          alt={t`Inbox menu icon`}
        />
      );
    }
    case t`Support`: {
      return (
        <img
          src={_.includes(pathname, iconLink) ? activeSupportIcon : inactiveSupportIcon}
          alt={t`Support menu icon`}
        />
      );
    }
    case t`Fleet`: {
      return (
        <div
          className={classNames('fleet-tab-icon-container', {
            'has-notification': rmaTicketCount > 0,
          })}
        >
          <img
            src={_.includes(pathname, iconLink) ? activeRmaIcon : inactiveRmaIcon}
            alt={t`RMA menu icon`}
          />
        </div>
      );
    }
    default: {
      return null;
    }
  }
};

export default TabIcon;
